import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { persistReducer, persistStore, PERSIST } from 'redux-persist'

import user from './slices/user_slice'
import terms from './slices/terms_slice'
import idin from './slices/idin_slice'
import notifications from './slices/notifications_slice'
import payments from './slices/payments_slice'
import policies from './slices/policies_slice'
import claims from './slices/claims_slice'
import memberBenefits from './slices/member_benefit_slice'
import support from './slices/support_slice'
import questions from './slices/questions_slice'
import tips from './slices/tips_slice'
import agency from './slices/agency_slice'
import products from './slices/products_slice'
import companies from './slices/companies_slice'
import resourceAuthorization from './slices/resource_authorization_slice'
import setup from './slices/setup_slice'
import deepLinks from './slices/deep_links_slice'
import biometrics from './slices/biometrics_slice'
import ivb from './slices/ivb_slice'
import valueMeterForms from './slices/value_meter_forms_slice'
import maintenance from './slices/maintenance_slice'
import relationMutations from './slices/relation_mutations_slice'
import appInfo from './slices/app_info_slice'
import temporaryData from './slices/temporary_data_slice'
import token from './slices/token_slice'

const rootReducer = combineReducers({
  user,
  terms,
  idin,
  notifications,
  payments,
  policies,
  claims,
  memberBenefits,
  support,
  questions,
  tips,
  agency,
  products,
  companies,
  resourceAuthorization,
  setup,
  deepLinks,
  biometrics,
  ivb,
  valueMeterForms,
  maintenance,
  relationMutations,
  appInfo,
  temporaryData,
  token
})

// Typing the store and persistor is difficult because they are created in a function
let storeInstance: any = null
let persistorInstance: any = null

export const createStore = (storage: any) => {
  if (storeInstance && persistorInstance) {
    console.error('Store has already been initialized.')

    return { store: storeInstance, persistor: persistorInstance }
  }

  const persistConfig = {
    key: 'root_v2',
    storage,
    version: 1,
    timeout: 0,
    blacklist: ['idin', 'deepLinks', 'maintenance', 'temporaryData']
  }

  const persistedReducer = persistReducer(persistConfig, rootReducer)

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [PERSIST]
        }
      })
  })

  const persistor = persistStore(store)

  storeInstance = store
  persistorInstance = persistor

  return { store, persistor }
}

export const getStore = () => {
  if (!storeInstance) {
    console.error('Store has not been initialized. Call createStore() first.')

    return null
  }

  return storeInstance
}

export const getPersistor = () => {
  if (!persistorInstance) {
    console.error('Persistor has not been initialized. Call createStore() first.')

    return null
  }

  return persistorInstance
}

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = ReturnType<typeof createStore>['store']['dispatch']
